import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupportDeskThread } from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { LiveChat } from '@/features/support/LiveChat';
import { useAlerts } from '@/lib/useAlerts';

/**
 * The support desk.
 *
 * ── TWO PANES, AND WHY ──────────────────────────────────────────────────────
 *
 * A queue on the left, one conversation on the right. An agent works a queue
 * by glancing at it constantly — who is waiting, who is typing, who has been
 * waiting longest — and a layout that hides the queue while a conversation is
 * open makes every one of those a navigation.
 *
 * On a phone it is one pane at a time, because two columns at 393px is two
 * unusable columns. The queue is the default, which is the right landing place
 * for somebody picking up the desk.
 *
 * ── THE QUEUE REFRESHES, THE CONVERSATION POLLS ─────────────────────────────
 *
 * They move at different speeds on purpose. The list every five seconds is
 * enough to notice somebody new; the open conversation every two is what makes
 * typing feel live. Polling the whole list at two seconds would be four times
 * the work for nothing an agent would notice.
 */
export function SupportDeskScreen() {
  const queryClient = useQueryClient();
  const [openId, setOpenId] = useState<number | null>(null);
  const [queue, setQueue] = useState<'all' | 'buyer' | 'seller'>('all');

  // Three tabs. `recent` is not a status — it is everything, newest first,
  // for the agent who remembers answering something yesterday and wants to
  // find it again without knowing how it ended.
  const [tab, setTab] = useState<'open' | 'closed' | 'recent'>('open');

  const { data, isLoading } = useQuery({
    queryKey: ['support-desk', queue, tab],
    queryFn: ({ signal }) =>
      api.supportDesk(
        { status: tab, ...(queue === 'all' ? {} : { queue }) },
        signal,
      ),
    // Slower than the open conversation's two seconds, on purpose: five is
    // enough to notice somebody new, and polling the whole list four times as
    // often would be four times the work for nothing an agent would see.
    refetchInterval: 5_000,
    refetchIntervalInBackground: false,
  });

  const rows = data?.threads ?? [];
  const counts = data?.counts ?? { open: 0, closed: 0 };
  const current = rows.find((row) => row.id === openId) ?? null;

  const waiting = rows.reduce((total, row) => total + (row.unread > 0 ? 1 : 0), 0);

  // ── THE DESK IS THE SCREEN THAT MOST NEEDS A SOUND ───────────────────────
  //
  // Somebody is waiting at the other end of it, and an agent working a queue
  // is looking at the open conversation rather than the list beside it. The
  // chime fires on an INCREASE only, so a desk opened with four waiting is
  // silent and the fifth is audible.
  //
  // Counted in MESSAGES, not threads: two people writing in is two alerts,
  // and a second message from somebody already in the queue is still news.
  const unreadMessages = rows.reduce((total, row) => total + row.unread, 0);

  // Only on the Open tab. Sounding an alert while somebody reads the archive
  // would be reporting history as news.
  useAlerts(unreadMessages, tab === 'open');

  return (
    <div className="mx-auto w-full max-w-[1200px] px-4 py-6 xl:px-6">
      <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="m-0 font-display text-[22px] font-bold tracking-[-0.02em]">Support</h1>
          <p className="m-0 mt-0.5 text-[12.5px] text-muted">
            {tab === 'open'
              ? waiting > 0
                ? `${waiting} waiting on a reply`
                : rows.length === 0
                  ? 'Nothing open'
                  : 'All answered'
              : tab === 'closed'
                ? `${counts.closed} archived`
                : 'Everything, newest first'}
          </p>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {([
            ['open', `Open${counts.open ? ` · ${counts.open}` : ''}`],
            ['closed', 'Closed'],
            ['recent', 'Recent'],
          ] as const).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => {
                setTab(key);
                setOpenId(null);
              }}
              data-testid={`tab-${key}`}
              className={cn(
                'min-h-9 rounded-pill border px-3 text-[12.5px] font-semibold',
                tab === key
                  ? 'border-blue bg-blue text-white'
                  : 'border-line bg-white text-muted hover:border-faint',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </header>

      <div className="mb-3 flex gap-1.5">
        {(['all', 'buyer', 'seller'] as const).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setQueue(key)}
            data-testid={`queue-${key}`}
            className={cn(
              'min-h-8 rounded-pill border px-2.5 text-[11.5px] font-semibold',
              queue === key
                ? 'border-blue bg-blue-tint text-blue'
                : 'border-line bg-white text-faint hover:border-faint',
            )}
          >
            {key === 'all' ? 'Everything' : key === 'buyer' ? 'Buyers' : 'Sellers'}
          </button>
        ))}
      </div>

      <div className="grid gap-3 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)]">
        {/* The queue. Hidden on a phone once a conversation is open. */}
        <section className={cn('min-w-0', current ? 'hidden lg:block' : 'block')}>
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-20 w-full" radius="card" />
              <Skeleton className="h-20 w-full" radius="card" />
            </div>
          ) : rows.length === 0 ? (
            <p className="rounded-card border border-line bg-white px-4 py-8 text-center text-[12.5px] text-faint">
              {tab === 'open' ? 'Nobody is waiting.' : 'Nothing here.'}
            </p>
          ) : (
            <ul data-testid="support-queue" className="m-0 list-none space-y-2 p-0">
              {rows.map((row) => (
                <QueueRow
                  key={row.id}
                  row={row}
                  active={row.id === openId}
                  onOpen={() => setOpenId(row.id)}
                />
              ))}
            </ul>
          )}
        </section>

        {/* The conversation. */}
        <section className={cn('min-w-0', current ? 'block' : 'hidden lg:block')}>
          {current ? (
            <Conversation
              key={current.id}
              thread={current}
              onBack={() => setOpenId(null)}
              onChanged={() => {
                void queryClient.invalidateQueries({ queryKey: ['support-desk'] });
              }}
            />
          ) : (
            <p className="hidden rounded-card border border-line bg-white px-4 py-16 text-center text-[12.5px] text-faint lg:block">
              Pick a conversation.
            </p>
          )}
        </section>
      </div>
    </div>
  );
}

function QueueRow({
  row,
  active,
  onOpen,
}: {
  row: SupportDeskThread;
  active: boolean;
  onOpen: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        data-testid="queue-row"
        className={cn(
          'w-full rounded-card border px-3.5 py-3 text-left',
          active ? 'border-blue bg-blue-tint-2' : 'border-line bg-white hover:border-faint',
        )}
      >
        <span className="flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-[13.5px] font-bold text-ink">
            {row.customer.name ?? 'Someone'}
          </span>

          {row.unread > 0 ? (
            <span
              data-testid="unread"
              className="grid h-[18px] min-w-[18px] shrink-0 place-items-center rounded-full bg-blue px-1 font-mono text-[10px] font-bold text-white"
            >
              {row.unread}
            </span>
          ) : null}
        </span>

        {/* The contact, on the row. It is what the widget asks for before the
            first message and the reason these are enquiries rather than
            conversations: one that ends unresolved gets followed up. */}
        {row.customer.contact ? (
          <span className="mt-0.5 block truncate text-[11.5px] text-muted">
            {row.customer.contact}
          </span>
        ) : null}

        <span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-faint">
          <span className="font-mono uppercase tracking-[0.08em]">{row.queue}</span>
          {row.customer.registered ? null : <span>· no account</span>}
          {row.waiting_since ? <span>· {sinceLabel(row.waiting_since)}</span> : null}
          {row.agent ? <span>· {row.agent}</span> : null}
        </span>

        {/* Typing, in the QUEUE. An agent scanning the list can see who is at
            their keyboard right now, which is who to pick up next. */}
        {row.typing ? (
          <span className="mt-1 block text-[11px] font-semibold text-blue">typing…</span>
        ) : null}
      </button>
    </li>
  );
}

function Conversation({
  thread,
  onBack,
  onChanged,
}: {
  thread: SupportDeskThread;
  onBack: () => void;
  onChanged: () => void;
}) {
  const id = thread.id;
  const [closing, setClosing] = useState(false);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      const data = await api.supportDeskThread(id, signal);

      return {
        messages: data.messages,
        othersTyping: data.customer_typing,
        status: data.thread.status,
      };
    },
    [id],
  );

  const poll = useCallback(
    async (after: number, signal?: AbortSignal) => {
      const data = await api.supportDeskPoll(id, after, signal);

      return {
        messages: data.messages,
        othersTyping: data.customer_typing,
        status: data.status,
      };
    },
    [id],
  );

  const send = useCallback(
    async (body: string) => {
      const message = await api.replyToSupport(id, body);

      onChanged();

      return message;
    },
    [id, onChanged],
  );

  const typing = useCallback(() => api.supportDeskTyping(id), [id]);

  // The mirror of the customer's rule: from the desk, a staff line is mine.
  const isMine = useCallback(
    (author: 'buyer' | 'seller' | 'staff' | 'system') => author === 'staff',
    [],
  );

  // What the widget collected first, then whatever the account carries. The
  // first is the one they asked to be reached on.
  const reach = useMemo(
    () =>
      [thread.customer.contact, thread.customer.phone, thread.customer.email]
        .filter(Boolean)
        .filter((one, i, all) => all.indexOf(one) === i)
        .join(' · '),
    [thread.customer.contact, thread.customer.phone, thread.customer.email],
  );

  return (
    <div className="flex min-h-0 flex-col">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex min-h-11 items-center gap-1.5 rounded-btn px-1 text-[12.5px] font-medium text-blue lg:hidden"
        >
          <Icon name="back" size={14} strokeWidth={2.4} />
          Queue
        </button>

        <div className="min-w-0 flex-1">
          <p className="m-0 truncate text-[14px] font-bold text-ink">
            {thread.customer.name ?? 'Someone'}
          </p>
          {/* The way to reach somebody whose chat has gone quiet. They are
              already on the order being discussed; `support.handle` carries a
              warning saying this is visible. */}
          {reach ? <p className="m-0 truncate text-[11.5px] text-faint">{reach}</p> : null}
        </div>

        {thread.status === 'open' ? (
          <Button
            size="sm"
            variant="grey"
            disabled={closing}
            onClick={() => {
              setClosing(true);
              void api
                .closeSupportThread(id)
                .then(onChanged)
                .finally(() => setClosing(false));
            }}
          >
            {closing ? 'Closing…' : 'Mark resolved'}
          </Button>
        ) : (
          <span className="rounded-pill bg-paper px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.1em] text-faint">
            Resolved
          </span>
        )}
      </div>

      <LiveChat
        className="h-[min(66vh,560px)]"
        load={load}
        poll={poll}
        send={send}
        typing={typing}
        channel={`support.thread.${id}`}
        isMine={isMine}
        otherParty={thread.customer.name ?? 'They'}
        placeholder="Reply"
        empty={<p className="m-0 text-[12.5px] text-faint">Nothing said yet.</p>}
      />
    </div>
  );
}

/** "4m" / "2h" / "3d". Precision past this is noise in a queue. */
function sinceLabel(iso: string): string {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 60 * 24) return `${Math.round(minutes / 60)}h`;

  return `${Math.round(minutes / (60 * 24))}d`;
}
